import { useCallback, useMemo } from 'react';

import { minutesToSeconds } from 'date-fns';

import isFunction from 'utils/function';
import { isEmpty } from 'utils/object';

import { TIMESHIFT_BUFFER } from 'screens/PlayerModal/hooks/useTimeshift';
import { PlayerError } from 'screens/PlayerModal/types';

type Params = {
    playerError: PlayerError;
    isLive: boolean;
    isCatchup: boolean;
    isVod: boolean;
    isNpvr: boolean;
    timeshiftModeOn: boolean;
    timeshiftPosition: number | null;
    timeshiftDuration: number | undefined;
    startoverAllowed: boolean;
    position: number;
    duration: number;
    livePosition?: number;
    airTimeStart?: number;
    airTimeEnd?: number;
    onSlidingStart?(): void;
    onSlidingComplete?(value: number): void;
};

function useBottomSliderProps({
    position = 0,
    duration = 0,
    livePosition,
    airTimeStart,
    airTimeEnd,
    isLive,
    isCatchup,
    isVod,
    isNpvr,
    timeshiftModeOn,
    timeshiftPosition,
    timeshiftDuration,
    startoverAllowed,
    playerError,
    onSlidingComplete,
    onSlidingStart,
}: Params) {
    const progressValue =
        isLive && (!airTimeStart || !airTimeEnd) ? 0 : position;
    const errorExists = !isEmpty(playerError);

    const minimumBufferPosition = useMemo(() => {
        if (errorExists) {
            return 0;
        }

        if (livePosition) {
            if (startoverAllowed) {
                return 0;
            }

            if (
                timeshiftModeOn &&
                timeshiftPosition &&
                timeshiftDuration != null
            ) {
                const timeshiftDurationSeconds =
                    timeshiftDuration === TIMESHIFT_BUFFER
                        ? timeshiftDuration
                        : minutesToSeconds(timeshiftDuration);

                return timeshiftDurationSeconds > 0
                    ? Math.max(
                          timeshiftPosition,
                          livePosition - timeshiftDurationSeconds,
                      )
                    : timeshiftPosition;
            }
        }

        return 0;
    }, [
        errorExists,
        timeshiftModeOn,
        livePosition,
        timeshiftPosition,
        timeshiftDuration,
        startoverAllowed,
    ]);

    const maximumBufferPosition = useMemo(() => {
        if (errorExists) {
            return 0;
        }

        // If the program has a live position and it is currently live, the guard time should not be taken into consideration.
        if (livePosition != null && isLive) {
            return Math.min(livePosition, duration);
        }

        // If the program has a live position, and it is not currently live (meaning it is a TS or SO), then the guard time should be taken into account during the calculation.
        if (livePosition != null && !isLive) {
            return Math.min(livePosition, duration);
        }

        if (isLive && startoverAllowed) {
            return progressValue;
        }

        if (isCatchup || isVod || isNpvr) {
            return duration;
        }

        return 0;
    }, [
        errorExists,
        livePosition,
        startoverAllowed,
        duration,
        progressValue,
        isLive,
        isCatchup,
        isVod,
        isNpvr,
    ]);

    const handleSlidingStart = useCallback(() => {
        if (isFunction(onSlidingStart)) {
            onSlidingStart();
        }
    }, [onSlidingStart]);

    const maximumValue = useMemo(() => Math.max(duration, 0), [duration]);

    const handleSlidingComplete = useCallback(
        (newValue: number) => {
            if (isFunction(onSlidingComplete)) {
                onSlidingComplete(newValue);
            }
        },
        [onSlidingComplete],
    );

    return {
        minimumBufferPosition,
        maximumBufferPosition,
        maximumValue,
        progressValue,
        errorExists,
        handleSlidingStart,
        handleSlidingComplete,
    };
}

export default useBottomSliderProps;
